import { X } from "lucide-react";
import { useId, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { cn } from "@/lib/utils";

/** Exact match first, then the "main" regional variant (fr → fr-fr), then prefixes, then the rest. */
function rankOf(value: string, needle: string): number {
  if (!needle) return 0;
  if (value === needle) return 0;
  if (value === `${needle}-${needle}`) return 1;
  if (value.startsWith(needle)) return 2;
  return 3;
}

export interface ChipSuggestion {
  value: string;
  label?: string;
  /** Extra content shown next to the suggestion and the chip (e.g. a badge). */
  hint?: ReactNode;
}

/**
 * Tag input: Enter / comma / Tab adds a value, Backspace removes the last one, suggestions are
 * filtered as you type. With `strict`, only suggested values can be added.
 */
export function ChipsInput({
  id,
  value,
  onChange,
  suggestions = [],
  placeholder,
  strict,
  normalize = (v) => v.trim(),
  loading,
}: {
  id?: string;
  value: string[];
  onChange: (value: string[]) => void;
  suggestions?: ChipSuggestion[];
  placeholder?: string;
  strict?: boolean;
  normalize?: (v: string) => string;
  loading?: boolean;
}) {
  const listId = useId();
  const input = useRef<HTMLInputElement>(null);
  const [text, setText] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);

  const byValue = useMemo(() => new Map(suggestions.map((s) => [s.value.toLowerCase(), s])), [suggestions]);
  const filtered = useMemo(() => {
    const needle = text.trim().toLowerCase();
    return suggestions
      .filter((s) => !value.some((v) => v.toLowerCase() === s.value.toLowerCase()))
      .filter((s) => !needle || s.value.toLowerCase().includes(needle) || s.label?.toLowerCase().includes(needle))
      .map((s, i) => ({ s, i, rank: rankOf(s.value.toLowerCase(), needle) }))
      .sort((a, b) => a.rank - b.rank || a.i - b.i)
      .map((x) => x.s)
      .slice(0, 50);
  }, [suggestions, text, value]);

  const add = (raw: string) => {
    const v = normalize(raw);
    if (!v) return;
    const known = byValue.get(v.toLowerCase());
    if (strict && !known) return;
    const final = known?.value ?? v;
    if (!value.some((x) => x.toLowerCase() === final.toLowerCase())) onChange([...value, final]);
    setText("");
    setActive(0);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === "," || (e.key === "Tab" && text)) {
      e.preventDefault();
      if (open && filtered[active] && (strict || text.trim() === "" || filtered[active]!.value.toLowerCase().startsWith(text.trim().toLowerCase()))) add(filtered[active]!.value);
      else add(text);
    } else if (e.key === "Backspace" && !text && value.length) {
      onChange(value.slice(0, -1));
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      setOpen(true);
      setActive((a) => Math.min(a + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div className="relative">
      <div
        className="flex min-h-9 w-full cursor-text flex-wrap items-center gap-1.5 rounded-md border bg-panel px-2 py-1.5 text-sm focus-within:outline-2 focus-within:outline-brand-500"
        onClick={() => input.current?.focus()}
      >
        {value.map((v) => {
          const s = byValue.get(v.toLowerCase());
          return (
            <span key={v} className="inline-flex items-center gap-1 rounded-full bg-muted py-0.5 pl-2.5 pr-1 text-xs font-medium">
              <span title={s?.label}>{v}</span>
              {s?.hint}
              <button
                type="button"
                className="rounded-full p-0.5 text-fg-muted hover:bg-border hover:text-fg"
                onClick={(e) => {
                  e.stopPropagation();
                  onChange(value.filter((x) => x !== v));
                }}
                aria-label={`Remove ${v}`}
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          );
        })}
        <input
          ref={input}
          id={id}
          value={text}
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          onChange={(e) => {
            setText(e.target.value);
            setOpen(true);
            setActive(0);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          onKeyDown={onKeyDown}
          onPaste={(e) => {
            const pasted = e.clipboardData.getData("text");
            if (/[,\n]/.test(pasted)) {
              e.preventDefault();
              const next = [...value];
              for (const part of pasted.split(/[,\n]/)) {
                const v = normalize(part);
                const known = byValue.get(v.toLowerCase());
                if (v && (!strict || known) && !next.some((x) => x.toLowerCase() === (known?.value ?? v).toLowerCase())) next.push(known?.value ?? v);
              }
              onChange(next);
            }
          }}
          placeholder={value.length ? "" : placeholder}
          className="min-w-24 flex-1 bg-transparent py-0.5 outline-none placeholder:text-fg-muted/70"
        />
      </div>
      {open && (filtered.length > 0 || loading) && (
        <ul id={listId} role="listbox" className="absolute z-20 mt-1 max-h-60 w-full overflow-y-auto rounded-md border bg-panel py-1 text-sm shadow-lg">
          {loading && <li className="px-3 py-1.5 text-fg-muted">…</li>}
          {filtered.map((s, i) => (
            <li
              key={s.value}
              role="option"
              aria-selected={i === active}
              onMouseDown={(e) => {
                e.preventDefault();
                add(s.value);
              }}
              onMouseEnter={() => setActive(i)}
              className={cn("flex cursor-pointer items-center justify-between gap-3 px-3 py-1.5", i === active && "bg-muted")}
            >
              <span>
                <span className="font-medium">{s.value}</span>
                {s.label && <span className="ml-2 text-fg-muted">{s.label}</span>}
              </span>
              {s.hint}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
